import React from 'react';
import { ButtonV2, Divider, Flex, LinkV2, SpacingStack, TextV2 } from '@withjoy/joykit';
import { Close } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { useTranslation } from '@shared/core';
import type { HotelSuggestDialogProps, SuggestionOptionId } from './HotelSuggestDialog.types';

const HOTEL_OPTIONS: readonly { id: SuggestionOptionId; icon: string }[] = [
  { id: 'mixBudgets', icon: '\u2696\uFE0F' },
  { id: 'mostlyAffordable', icon: '\uD83D\uDCB0' },
  { id: 'mostlyUpscale', icon: '\u2728' },
  { id: 'whateverClosest', icon: '\uD83D\uDCCD' },
];

const NEIGHBORHOOD_OPTION: { id: SuggestionOptionId; icon: string } = { id: 'suggestNeighborhoods', icon: '\uD83D\uDDFA\uFE0F' };

export const HotelSuggestDialog: React.FC<HotelSuggestDialogProps> = ({ isOpen, onClose, onSelect }) => {
  const { tq } = useTranslation('accommodationsAdmin');

  const labelMap: Record<SuggestionOptionId, string> = {
    mixBudgets: tq`A mix for different budgets`,
    mostlyAffordable: tq`More affordable`,
    mostlyUpscale: tq`More upscale`,
    whateverClosest: tq`Closer`,
    suggestNeighborhoods: tq`I'd rather just suggest neighborhoods`,
  };

  const descriptionMap: Record<SuggestionOptionId, string> = {
    mixBudgets: tq`Some affordable, some upscale`,
    mostlyAffordable: tq`Budget-friendly options`,
    mostlyUpscale: tq`Higher-end hotels`,
    whateverClosest: tq`Convenience over everything`,
    suggestNeighborhoods: tq`Skip specific hotels — point guests to the right areas`,
  };

  const renderOption = (option: { id: SuggestionOptionId; icon: string }) => (
    <ButtonV2
      key={option.id}
      variant="outline"
      intent="neutral"
      shape="rounded"
      onClick={() => onSelect({ id: option.id })}
      width="100%"
      justifyContent="flex-start"
      textAlign="left"
      height="auto"
      paddingY={4}
      paddingX={5}
      borderColor="mono3"
      whiteSpace="normal"
    >
      <Flex
        alignItems="flex-start"
        gap={4}
        width="100%"
      >
        <TextV2
          fontSize={pxToRem(20)}
          flexShrink={0}
        >
          {option.icon}
        </TextV2>
        <Flex
          flexDirection="column"
          gap={0}
          minWidth={0}
          flex={1}
        >
          <TextV2
            typographyVariant="hed1"
            fontWeight="medium"
            color="mono14"
          >
            {labelMap[option.id]}
          </TextV2>
          <TextV2
            typographyVariant="label2"
            color="mono8"
          >
            {descriptionMap[option.id]}
          </TextV2>
        </Flex>
      </Flex>
    </ButtonV2>
  );

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onClose}
      hideDivider
      removeBodyPadding
      hasCloseButton={false}
      drawerOverrides={{
        ...drawerOverrides,
        Body: { props: { ...drawerOverrides.Body?.props, padding: 0 } },
      }}
      dialogOverrides={{
        Content: { props: { width: ['100%', pxToRem(480)], borderRadius: pxToRem(16) } },
        Body: { props: { paddingX: 0, paddingY: 0 } },
      }}
    >
      <Flex
        width="100%"
        flexDirection="column"
        position="relative"
        padding={{ __: 6, sm2: 8 }}
        paddingBottom={8}
        gap={6}
      >
        {/* Close Button */}
        <Flex
          justifyContent="flex-end"
          position="absolute"
          top={5}
          right={5}
        >
          <ButtonV2
            variant="ghost"
            intent="neutral"
            onClick={onClose}
            aria-label={tq`Close`}
            padding={0}
            width={pxToRem(16)}
            minWidth={pxToRem(16)}
            height={pxToRem(16)}
          >
            <Close size={16} />
          </ButtonV2>
        </Flex>

        <SpacingStack spacing={2}>
          <TextV2
            typographyVariant={{ __: 'hed4', sm2: 'hed5' }}
            color="mono14"
            fontWeight="semibold"
          >
            {tq`What kind of hotels should we add?`}
          </TextV2>
          <TextV2
            typographyVariant="body1"
            color="mono14"
          >
            {tq`Tell us what you're looking for and we'll suggest better matches.`}
          </TextV2>
        </SpacingStack>

        <Flex
          flexDirection="column"
          gap={3}
        >
          {HOTEL_OPTIONS.map(renderOption)}
        </Flex>

        <Divider />
        {renderOption(NEIGHBORHOOD_OPTION)}

        <LinkV2
          as="button"
          typographyVariant="body2"
          color="mono8"
          onClick={onClose}
          cursor="pointer"
          textAlign="center"
        >
          {tq`No thanks, I'll keep editing`}
        </LinkV2>
      </Flex>
    </ResponsiveDialog>
  );
};
